import React, { forwardRef } from "react";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import "./DashlyPickerTrigger.scss";

export type DashlyPickerTriggerProps = {
    value?: React.ReactNode;
    placeholder?: string;
    onClick?: () => void;
    disabled?: boolean;
    trailingIcon?: string;
    leadingIcon?: string;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    className?: string;
    containerClassName?: string;
    id?: string;
    "aria-label"?: string;
};

const DashlyPickerTrigger = forwardRef<HTMLButtonElement, DashlyPickerTriggerProps>(function DashlyPickerTrigger(
    {
        value,
        placeholder,
        onClick,
        disabled = false,
        trailingIcon = "pi pi-pencil",
        leadingIcon,
        label,
        labelInfo,
        hint,
        required,
        className,
        containerClassName,
        id,
        "aria-label": ariaLabel,
    },
    ref
) {
    const hasValue = value !== null && value !== undefined && `${value}`.trim().length > 0;

    const control = (
        <button
            ref={ref}
            type="button"
            id={id}
            className={classNames(
                "dashly-picker-trigger",
                !hasValue && "dashly-picker-trigger--placeholder",
                disabled && "dashly-picker-trigger--disabled",
                className
            )}
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
        >
            {leadingIcon ? <i className={classNames("dashly-picker-trigger__leading-icon", leadingIcon)} aria-hidden /> : null}
            <span className="dashly-picker-trigger__value">{hasValue ? value : placeholder}</span>
            {trailingIcon ? <i className={classNames("dashly-picker-trigger__trailing-icon", trailingIcon)} aria-hidden /> : null}
        </button>
    );

    if (!label) {
        return control;
    }

    return (
        <DashlyFieldShell label={label} labelInfo={labelInfo} hint={hint} htmlFor={id} required={required} className={containerClassName}>
            {control}
        </DashlyFieldShell>
    );
});

DashlyPickerTrigger.displayName = "DashlyPickerTrigger";

export { DashlyPickerTrigger };
